import {Injectable} from "@angular/core";
import {ActivityState, ConnectivityState, MappingBase} from "@models";
import {Translations} from "@translations";
import {TranslateService} from "@ngx-translate/core";
import {NotificationService, SimConfigurationService, SimMaterialDialogService, SimSystemExtendedService} from "@services";

/**
 * Do not use as a singleton, as it contains state
 */
@Injectable()
export class SimWakeOnLanRequestRetryService {
	T = Translations;

	// a mapping from the playerMappingId to the amount of times a start request was sent to the player
	protected _cachedWolRequests: Map<string, number> = new Map<string, number>();

	constructor(
		private systemService: SimSystemExtendedService,
		private notificationService: NotificationService,
		private translate: TranslateService,
		private dialogService: SimMaterialDialogService,
	) {}

	public canWakeUp(device: MappingBase) {
		let can = device?.webSocket?.activityState !== ActivityState.Active && device?.connectivityStatus === ConnectivityState.Offline;
		can &&= !!device?.deviceUsername && !!device?.devicePassword;
		return can;
	}

	public canShutDown(device: MappingBase) {
		let can = device?.webSocket?.activityState === ActivityState.Active || device?.connectivityStatus === ConnectivityState.Online;
		can &&= !!device?.subnetMask && !!device?.macAddress;
		return can;
	}

	public async wakeUpMappedDevice(element: MappingBase) {
		const confirmationResult = await this.dialogService.openConfirmationDialog({
			title: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_ON_DIALOG_TITLE),
			description: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_ON_DIALOG_DESCRIPTION),
			cancelText: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_ON_DIALOG_CANCEL_BUTTON),
			okText: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_ON_DIALOG_OK_BUTTON),
		});

		if (confirmationResult.action === "ok") {
			await this.sendWakeOnLanRequest(element);
		}
	}

	public async shutDownMappedDevice(element: MappingBase, cannotBePoweredOnRemotely: boolean = false) {
		const confirmationResult = await this.dialogService.openConfirmationDialog({
			title: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_OFF_DIALOG_TITLE),
			description: cannotBePoweredOnRemotely
				? this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_OFF_TABLET_DESCRIPTION)
				: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_OFF_DIALOG_DESCRIPTION),
			cancelText: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_OFF_DIALOG_CANCEL_BUTTON),
			okText: this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_POWER_OFF_DIALOG_OK_BUTTON),
		});

		if (confirmationResult.action === "ok") {
			const response = await this.systemService.shutdownMappedDevice({
				mappedDeviceId: element.id,
			});

			if (response.success) {
				this.notificationService.notifySuccess(this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_DEVICE_SUCCESSFUL_POWER_OFF));
			} else {
				this.notificationService.notifyError(this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_DEVICE_COULD_NOT_POWER_OFF));
			}
		}
	}

	private async sendStartRequestAndCache(element: MappingBase) {
		const response = await this.systemService.wakeMappedDevice({
			mappedDeviceId: element.id,
		});

		if (!response.success) {
			console.error(response.reason);
			this.notificationService.notifyError(this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_DEVICE_COULD_NOT_POWER_ON));
			this._cachedWolRequests.delete(element.id);
		} else {
			this.notificationService.notifySuccess(this.translate.instant(this.T.MEDIA_DROP_DOWN_NOTIFYERROR_START_IN_MOMENT));
			setTimeout(() => {
				const currentWolRequestCount = this._cachedWolRequests.get(element.id);
				if (currentWolRequestCount === undefined) {
					// Request was successful
					this.notificationService.notifySuccess(
						this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_DEVICE_SUCCESSFUL_POWER_ON) + ` ${element.name}`,
					);
					return;
				}

				if (currentWolRequestCount < SimConfigurationService.settings.wakeOnLanMaxRetries) {
					console.log(
						`Retrying wake on lan request for ${element.name} (${currentWolRequestCount + 1} of ${SimConfigurationService.settings.wakeOnLanMaxRetries})`,
					);
					this._cachedWolRequests.set(element.id, currentWolRequestCount + 1);
					return this.sendStartRequestAndCache(element);
				} else {
					this.notificationService.notifyError(
						this.translate.instant(this.T.SYSTEM_STATE_OVERVIEW_DEVICE_COULD_NOT_POWER_ON) + ` ${element.name}`,
					);
					this._cachedWolRequests.delete(element.id);
				}
			}, SimConfigurationService.settings.wakeOnLanRetryInterval);
		}
	}

	private async sendWakeOnLanRequest(element: MappingBase) {
		this._cachedWolRequests.set(element.id, 0);
		return this.sendStartRequestAndCache(element);
	}

	/**
	 * Should be used with the mappingChanged events
	 */
	public handleStateUpdate(newMappingState: MappingBase) {
		if (!newMappingState) return;

		if (this._cachedWolRequests.has(newMappingState.id) && newMappingState.connectivityStatus === ConnectivityState.Online) {
			this._cachedWolRequests.delete(newMappingState.id);
		}
	}

	public hasActiveWakeOnLanRequest(element: MappingBase) {
		return this._cachedWolRequests.has(element.id);
	}
}
